import { KlearDate } from '@klear/date'
import { Money, formatPerCurrencyTotals, perCurrencyTotals } from '@klear/money'
import head from 'lodash/head'
import isString from 'lodash/isString'
import type { OrderListSummary } from '@klear/order/order-list'

export type OrderListSummarySource = { amount: Money; effectiveDate: KlearDate }

export function orderListSummaryFromPurchaseOrders(
  purchaseOrders: readonly OrderListSummarySource[]
): OrderListSummary | undefined {
  if (purchaseOrders.length === 0) {
    return undefined
  }
  const sorted = [...purchaseOrders].sort((a, b) => KlearDate.compare(b.effectiveDate, a.effectiveDate))
  const primary = head(sorted)!

  return {
    orderValue: perCurrencyTotals(purchaseOrders.map((po) => po.amount)),
    effectiveDate: primary.effectiveDate
  }
}

export type FormatOrderDisplayNameInput = {
  customerOrganizationName?: string
  summary?: OrderListSummary
  targetDeliveryDate?: KlearDate | string
  totalInvoicedAmount?: Money
  customerName?: boolean
  effectiveDate?: boolean
  deliveryDate?: boolean
  orderValue?: boolean
  totalInvoiced?: boolean
}

export function summaryOrderValues(summary: OrderListSummary): Money[] {
  return summary.orderValue
}

export function formatSummaryOrderValue(summary: OrderListSummary | undefined): string {
  if (summary == null) return '—'
  return formatPerCurrencyTotals(summaryOrderValues(summary).filter((money) => !money.isZero()))
}

export function summaryOrderValueSortKey(summary: OrderListSummary | undefined): Money {
  return Money.max(...(summary != null ? summaryOrderValues(summary) : []))
}

function summaryEffectiveDateYmd(summary: OrderListSummary): string {
  return summary.effectiveDate.toJSON()
}

export function formatOrderDisplayName({
  customerOrganizationName,
  summary,
  targetDeliveryDate,
  totalInvoicedAmount,
  customerName = true,
  effectiveDate = true,
  deliveryDate = true,
  orderValue = true,
  totalInvoiced = true
}: FormatOrderDisplayNameInput = {}): string {
  const pieces: string[] = []

  if (customerName && customerOrganizationName) {
    pieces.push(customerOrganizationName)
  }
  if (effectiveDate && summary) {
    pieces.push(`start: ${KlearDate.format(KlearDate.parse(summaryEffectiveDateYmd(summary)))}`)
  }
  if (deliveryDate && targetDeliveryDate) {
    const ymd = isString(targetDeliveryDate) ? targetDeliveryDate : targetDeliveryDate.toJSON()
    pieces.push(`delivery: ${KlearDate.format(KlearDate.parse(ymd))}`)
  }
  if (orderValue && summary) {
    const values = summaryOrderValues(summary).filter((money) => !money.isZero())
    if (values.length > 0) {
      pieces.push(`order: ${formatPerCurrencyTotals(values)}`)
    }
  }
  if (totalInvoiced && totalInvoicedAmount && !totalInvoicedAmount.isZero()) {
    pieces.push(`invoiced: ${totalInvoicedAmount}`)
  }

  return pieces.join(' | ')
}
